import React, { useState, useEffect, useCallback} from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { 
  Briefcase, Plus, Search, Eye, CheckCircle2, XCircle, Clock, 
  AlertTriangle, Filter, RefreshCw
} from 'lucide-react';
import toast from 'react-hot-toast';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const SESList = () => {
  const navigate = useNavigate();
  const { canWrite } = useAuth();
  const [loading, setLoading] = useState(true);
  const [entries, setEntries] = useState([]);
  const [filteredEntries, setFilteredEntries] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('ALL');

  useEffect(() => {
    loadEntries();
  }, []);


  const loadEntries = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${BACKEND_URL}/api/service-entries`);
      setEntries(response.data);
    } catch (error) {
      console.error('Error loading service entries:', error);
      toast.error('Failed to load service entries');
    } finally {
      setLoading(false);
    }
  };

  const filterEntries = useCallback(() => {
    let filtered = [...entries];

    if (statusFilter !== 'ALL') {
      filtered = filtered.filter(e => e.status === statusFilter);
    }

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(e =>
        e.entry_sheet_number?.toLowerCase().includes(term) ||
        e.po_number?.toLowerCase().includes(term) ||
        e.vendor_id?.toLowerCase().includes(term) ||
        e.vendor_name?.toLowerCase().includes(term)
      );
    }

    setFilteredEntries(filtered);
  }, [entries, searchTerm, statusFilter]);

  useEffect(() => {
    filterEntries();
  }, [filterEntries]);

  const getStatusColor = (status) => {
    const colors = {
      'CREATED': 'bg-yellow-100 text-yellow-800',
      'PARTIALLY_ACCEPTED': 'bg-orange-100 text-orange-800',
      'ACCEPTED': 'bg-green-100 text-green-800',
      'REJECTED': 'bg-red-100 text-red-800',
      'INVOICED': 'bg-orange-100 text-orange-800',
      'CANCELLED': 'bg-gray-100 text-gray-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'CREATED': return <Clock className="w-4 h-4" />;
      case 'ACCEPTED': return <CheckCircle2 className="w-4 h-4" />;
      case 'PARTIALLY_ACCEPTED': return <AlertTriangle className="w-4 h-4" />;
      case 'REJECTED': return <XCircle className="w-4 h-4" />;
      case 'INVOICED': return <CheckCircle2 className="w-4 h-4" />;
      default: return <Clock className="w-4 h-4" />;
    }
  };

  return (
    <Layout>
      <div className="p-6 max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <Briefcase className="w-7 h-7 text-orange-600" />
              Service Entry Sheets
            </h1>
            <p className="text-sm text-gray-500">ML81N - Manage service entries and acceptances</p>
          </div>
          {canWrite('service_entry') && (
          <Button onClick={() => navigate('/p2p/ses-create')} className="bg-orange-600 hover:bg-orange-700">
            <Plus className="w-4 h-4 mr-2" />
            Create Entry Sheet
          </Button>
          )}
        </div>

        {/* Filters */}
        <div className="bg-white rounded-lg shadow-sm border p-4 mb-6">
          <div className="flex flex-wrap gap-4 items-end">
            <div className="flex-1 min-w-[200px] max-w-md">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <Input
                  placeholder="Search by SES number, PO, vendor..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Filter className="w-4 h-4 text-gray-400" />
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-md text-sm"
              >
                <option value="ALL">All Status</option>
                <option value="CREATED">Created (Pending)</option>
                <option value="ACCEPTED">Accepted</option>
                <option value="PARTIALLY_ACCEPTED">Partially Accepted</option>
                <option value="REJECTED">Rejected</option>
                <option value="INVOICED">Invoiced</option>
                <option value="CANCELLED">Cancelled</option>
              </select>
            </div>
            <Button variant="outline" size="sm" onClick={loadEntries}>
              <RefreshCw className="w-4 h-4 mr-1" />
              Refresh
            </Button>
          </div>
        </div>

        {/* Results */}
        <div className="bg-white rounded-lg shadow-sm border">
          {loading ? (
            <div className="p-8 text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-600 mx-auto mb-4"></div>
              <p className="text-gray-600">Loading service entries...</p>
            </div>
          ) : filteredEntries.length === 0 ? (
            <div className="p-8 text-center">
              <Briefcase className="w-12 h-12 mx-auto mb-4 text-gray-300" />
              <p className="text-gray-600">No service entries found</p>
              {canWrite('service_entry') && (
              <Button onClick={() => navigate('/p2p/ses-create')} className="mt-4" variant="outline">
                Create First Entry Sheet
              </Button>
              )}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Entry Sheet</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">PO Number</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Vendor</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Total Value</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Accepted %</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Entry Date</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {filteredEntries.map((entry) => (
                    <tr key={`${entry.entry_sheet_number}-${entry.fiscal_year}`} className="hover:bg-gray-50">
                      <td className="px-4 py-3">
                        <span className="font-medium text-orange-600">{entry.entry_sheet_number}</span>
                        <span className="text-xs text-gray-500 block">/ {entry.fiscal_year}</span>
                      </td>
                      <td className="px-4 py-3 font-medium text-gray-900">{entry.po_number}</td>
                      <td className="px-4 py-3">
                        <p className="text-gray-900">{entry.vendor_id}</p>
                        <p className="text-xs text-gray-500 truncate max-w-[150px]">{entry.vendor_name}</p>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(entry.status)}`}>
                          {getStatusIcon(entry.status)}
                          {entry.status}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right font-medium">
                        {entry.currency} {(entry.total_gross_value || 0).toLocaleString(undefined, {minimumFractionDigits: 2})}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex items-center justify-end gap-2">
                          <div className="w-16 bg-gray-200 rounded-full h-2">
                            <div
                              className="bg-green-500 h-2 rounded-full"
                              style={{ width: `${entry.acceptance_percentage || 0}%` }}
                            />
                          </div>
                          <span className="text-xs text-gray-600">{(entry.acceptance_percentage || 0).toFixed(0)}%</span>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-gray-600">{entry.entry_date}</td>
                      <td className="px-4 py-3">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => navigate(`/p2p/ses-view/${entry.entry_sheet_number}/${entry.fiscal_year}`)}
                          className="text-orange-600 hover:text-orange-700"
                        >
                          <Eye className="w-4 h-4 mr-1" />
                          View
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Summary */}
        <div className="mt-4 text-sm text-gray-500">
          Showing {filteredEntries.length} of {entries.length} entries
        </div>
      </div>
    </Layout>
  );
};

export default SESList;
